/**
 * OperatorQueueTable — F-007 Phase 4 (IMPL-P4-005).
 *
 * Tabella della coda operatore. Una riga per pratica con:
 *   - ID (troncato per leggibilità, full ID nel `title`)
 *   - tipologia, stato (badge), createdAt, submittedAt
 *   - bottone "Prendi in carico" che chiama `onTake(item.id)`.
 *
 * Race-condition UX:
 *   - `aria-disabled={takePending}` finché la mutation è in volo (evita
 *     doppi click). `onTake` è una callback fornita dalla page (via
 *     `useTakeInCarico().mutate`).
 *
 * NB: il design system non espone ancora un componente `Table` shadcn —
 * usiamo un `<table>` HTML stilizzato con Tailwind, allineato al resto
 * delle dashboard (cfr. AppLayout).
 */
import { Link } from 'react-router-dom';
import type { OperatorQueueItem } from '@app/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface OperatorQueueTableProps {
  items: OperatorQueueItem[];
  onTake: (praticaId: string) => void;
  takePending?: boolean;
  /**
   * Quando una specifica riga è in volo (mutation `take` per quell'id),
   * disabilita solo il bottone di quella riga. Se non passato, ricade su
   * `takePending`.
   */
  pendingId?: string | null;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortId(id: string): string {
  return id.length <= 10 ? id : `${id.slice(0, 8)}…`;
}

export function OperatorQueueTable({
  items,
  onTake,
  takePending = false,
  pendingId = null,
}: OperatorQueueTableProps) {
  if (items.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        Nessuna pratica in coda.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 font-medium">
              ID
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Tipologia
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Stato
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Inviata
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Azione
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((item) => {
            const rowPending = pendingId === item.id || takePending;
            return (
              <tr key={item.id} className="hover:bg-muted/30">
                <td className="px-3 py-2 font-mono text-xs" title={item.id}>
                  <Link
                    to={`/operatore/${item.id}`}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {shortId(item.id)}
                  </Link>
                </td>
                <td className="px-3 py-2">{item.tipologia}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline">{item.stato}</Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatDateTime(item.submittedAt)}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onTake(item.id)}
                    aria-disabled={rowPending}
                    disabled={rowPending}
                  >
                    Prendi in carico
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
